/**
 * openLots — player lots that are not curated landmarks (ADR-0336).
 *
 * A lot is a playable / paint-able subject hanging off baked geometry: an OSM
 * pitch (`pitch:<district>:<index>`), a carnival booth (`booth:<planet>:<id>`),
 * or a standing cell (`cell:<district>:<qLon>:<qLat>`). Pure: no three, no DOM,
 * no clock. Pitch paints recolor turf; booth/cell paints become overlay discs.
 */
import type { PitchKind } from './pitchKinds.ts';
import type { SurfaceMaterial } from './planetModel.ts';

export const PITCH_LOT_PREFIX = 'pitch:';
export const BOOTH_LOT_PREFIX = 'booth:';
export const CELL_LOT_PREFIX = 'cell:';
/** ~22 m at the equator — a standing cell, not a DEM sculpt. */
export const CELL_STEP_DEG = 0.0002;
export const CELL_SPAN_M = 24;
/** Cap overlay discs so a long session cannot flood the scene. */
export const FREEDOM_PATCH_CAP = 48;

/** Closed paint cycle — maps onto turf hexes, never widens SurfaceMaterial. */
export const LOT_PAINTS = ['vegetation', 'sand', 'plaza', 'road', 'water'] as const;
export type LotPaint = (typeof LOT_PAINTS)[number];

export const LOT_PAINT_HEX: Readonly<Record<LotPaint, string>> = {
  vegetation: '#5a9a4e',
  sand: '#e6d2a0',
  plaza: '#c4b8a8',
  road: '#5c5c5c',
  water: '#3f9fd8',
};

export const SPORT_KITS = ['home', 'away', 'night'] as const;
export type SportKit = (typeof SPORT_KITS)[number];

/**
 * Closed kit palette (ADR-0336 kit slice). Three jersey hexes only — never a
 * free-form colour and never a new SurfaceMaterial. Home reads as whites,
 * away as a match crimson, night as floodlit navy.
 */
export const SPORT_KIT_HEX: Readonly<Record<SportKit, string>> = {
  home: '#f4f7fb',
  away: '#c81e3a',
  night: '#24315e',
};

/** Exhaustive SportKit → hex. A new kit fails typecheck until it has a colour. */
export function sportKitHex(kit: SportKit): string {
  switch (kit) {
    case 'home':
      return SPORT_KIT_HEX.home;
    case 'away':
      return SPORT_KIT_HEX.away;
    case 'night':
      return SPORT_KIT_HEX.night;
    default: {
      const _exhaustive: never = kit;
      return _exhaustive;
    }
  }
}

const EARTH_R = 6371000;

export function metresBetween(
  a: { readonly lon: number; readonly lat: number },
  b: { readonly lon: number; readonly lat: number },
): number {
  const p1 = (a.lat * Math.PI) / 180;
  const p2 = (b.lat * Math.PI) / 180;
  const dP = p2 - p1;
  const dL = ((b.lon - a.lon) * Math.PI) / 180;
  const s =
    Math.sin(dP / 2) * Math.sin(dP / 2) + Math.cos(p1) * Math.cos(p2) * Math.sin(dL / 2) * Math.sin(dL / 2);
  return 2 * EARTH_R * Math.atan2(Math.sqrt(s), Math.sqrt(Math.max(0, 1 - s)));
}

export function pitchLotId(districtId: string, index: number): string {
  return `${PITCH_LOT_PREFIX}${districtId}:${index}`;
}

export function parsePitchLotId(id: string): { readonly districtId: string; readonly index: number } | null {
  const m = /^pitch:([a-z0-9-]+):(\d+)$/.exec(id);
  if (!m) return null;
  return { districtId: m[1], index: Number(m[2]) };
}

export function boothLotId(planetId: string, boothId: string): string {
  return `${BOOTH_LOT_PREFIX}${planetId}:${boothId}`;
}

export function cellLotId(districtId: string, lon: number, lat: number): string {
  const qLon = Math.round(lon / CELL_STEP_DEG);
  const qLat = Math.round(lat / CELL_STEP_DEG);
  return `${CELL_LOT_PREFIX}${districtId}:${qLon}:${qLat}`;
}

export function parseCellLotId(id: string): { readonly districtId: string; readonly lon: number; readonly lat: number } | null {
  const m = /^cell:([a-z0-9-]+):(-?\d+):(-?\d+)$/.exec(id);
  if (!m) return null;
  return {
    districtId: m[1],
    lon: Number(m[2]) * CELL_STEP_DEG,
    lat: Number(m[3]) * CELL_STEP_DEG,
  };
}

export function parseBoothLotId(id: string): { readonly planetId: string; readonly boothId: string } | null {
  const m = /^booth:([a-z0-9-]+):([a-z0-9-]+)$/.exec(id);
  if (!m) return null;
  return { planetId: m[1], boothId: m[2] };
}

export function isLotPaint(v: unknown): v is LotPaint {
  return typeof v === 'string' && (LOT_PAINTS as readonly string[]).includes(v);
}

export function isSportKit(v: unknown): v is SportKit {
  return typeof v === 'string' && (SPORT_KITS as readonly string[]).includes(v);
}

export function nextLotPaint(current: LotPaint | null | undefined): LotPaint {
  const i = current ? LOT_PAINTS.indexOf(current) : -1;
  return LOT_PAINTS[(i + 1) % LOT_PAINTS.length];
}

export function nextSportKit(current: SportKit | null | undefined): SportKit {
  const i = current ? SPORT_KITS.indexOf(current) : -1;
  return SPORT_KITS[(i + 1) % SPORT_KITS.length];
}

/** Paint that is a real SurfaceMaterial keeps that name; water stays a turf hex only. */
export function paintAsSurface(paint: LotPaint): SurfaceMaterial | null {
  if (paint === 'water') return null;
  return paint;
}

export interface GeoLot {
  readonly lon: number;
  readonly lat: number;
  readonly spanM: number;
}

export interface NearLot<T extends GeoLot> {
  readonly lot: T;
  readonly distM: number;
  readonly onLot: boolean;
}

/** Nearest lot within its own span (plus a short approach apron). */
export function nearestLot<T extends GeoLot>(
  here: { readonly lon: number; readonly lat: number },
  lots: readonly T[],
  apronM = 12,
): NearLot<T> | null {
  let best: NearLot<T> | null = null;
  for (const lot of lots) {
    const distM = metresBetween(here, lot);
    const reach = Math.max(lot.spanM * 0.55 + apronM, 24);
    if (distM > reach) continue;
    if (!best || distM < best.distM) {
      best = { lot, distM, onLot: distM <= lot.spanM * 0.55 };
    }
  }
  return best;
}

export function kindLabel(kind: PitchKind): string {
  return kind.replace(/-/g, ' ');
}

export interface FreedomPatch {
  readonly lon: number;
  readonly lat: number;
  readonly hex: string;
  readonly spanM: number;
}

/**
 * Discs for booth + standing-cell paints. Pitch paints stay on the turf mesh
 * (`recolorGround`) so this list never double-paints a football pitch.
 */
export function overlayPatchesFor(
  districtId: string,
  paints: Readonly<Record<string, LotPaint>>,
  booths: ReadonlyArray<{ readonly id: string; readonly lon: number; readonly lat: number; readonly spanM: number }>,
): readonly FreedomPatch[] {
  const out: FreedomPatch[] = [];
  const boothById = new Map(booths.map((b) => [b.id, b]));
  const pitchPrefix = `${PITCH_LOT_PREFIX}${districtId}:`;
  const boothPrefix = `${BOOTH_LOT_PREFIX}${districtId}:`;
  const cellPrefix = `${CELL_LOT_PREFIX}${districtId}:`;
  for (const [id, paint] of Object.entries(paints)) {
    if (out.length >= FREEDOM_PATCH_CAP) break;
    const hex = LOT_PAINT_HEX[paint];
    if (!hex) continue;
    if (id.startsWith(pitchPrefix)) continue;
    if (id.startsWith(boothPrefix)) {
      const parsed = parseBoothLotId(id);
      const booth = parsed ? boothById.get(parsed.boothId) : undefined;
      if (booth) out.push({ lon: booth.lon, lat: booth.lat, hex, spanM: booth.spanM });
      continue;
    }
    if (id.startsWith(cellPrefix)) {
      const parsed = parseCellLotId(id);
      if (parsed) out.push({ lon: parsed.lon, lat: parsed.lat, hex, spanM: CELL_SPAN_M });
    }
  }
  return out;
}

/** Standing-cell paints that may pull street life onto civic lots. Booth / pitch
 *  paints stay out (carnival attendants and turf recolor are other stories). */
export interface OverlayLingerPaint {
  readonly lon: number;
  readonly lat: number;
  readonly paint: LotPaint;
}

export function overlayLingerPaintsFor(
  districtId: string,
  paints: Readonly<Record<string, LotPaint>>,
): readonly OverlayLingerPaint[] {
  const out: OverlayLingerPaint[] = [];
  const cellPrefix = `${CELL_LOT_PREFIX}${districtId}:`;
  for (const [id, paint] of Object.entries(paints)) {
    if (out.length >= FREEDOM_PATCH_CAP) break;
    if (!id.startsWith(cellPrefix)) continue;
    const parsed = parseCellLotId(id);
    if (!parsed) continue;
    out.push({ lon: parsed.lon, lat: parsed.lat, paint });
  }
  return out;
}
